import React, { useState, useEffect } from 'react';
import { Table, Card, Spin, message, Tag, DatePicker } from 'antd';
import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import GetTimesheetsByPlanApi from '../../api/timesheets/GetTimesheetsByPlanApi';

const { RangePicker } = DatePicker;

function PlanTimesheetSummary({ planId, data }) {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('month')
  ]);

  useEffect(() => {
    if (planId) {
      loadTimesheets();
    }
  }, [planId, dateRange]);

  const loadTimesheets = async () => {
    setLoading(true);
    try {
      const response = await GetTimesheetsByPlanApi(planId);
      
      if (response.success) {
        // Filter by date range if specified
        let filteredTimesheets = response.data || [];
        
        if (dateRange && dateRange[0] && dateRange[1]) {
          filteredTimesheets = filteredTimesheets.filter(timesheet => {
            const workDate = moment(timesheet.workDate);
            return workDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
          });
        }
        setTimesheets(filteredTimesheets);
      } else {
        message.error(response.message || 'Failed to load timesheet data');
      }
    } catch (error) {
      console.error('Error loading timesheet data:', error);
      message.error('Failed to load timesheet data');
    }
    setLoading(false);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'workDate',
      key: 'workDate',
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.workDate).unix() - moment(b.workDate).unix(),
      width: 120,
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => {
        const plan = record.plan;
        const isFromSplitPlan = plan?.isFromSplitPlan;
        
        return (
          <div className="text-sm">
            <div className="font-medium">{plan?.title || 'Unknown Plan'}</div>
            {isFromSplitPlan && (
              <Tag color="orange" size="small">Split Plan</Tag>
            )}
          </div>
        );
      },
      width: 180,
    },
    {
      title: 'Employee',
      key: 'user',
      render: (_, record) => {
        const user = record.user;
        
        let displayName = 'Unknown User';
        if (user) {
          const firstName = user.firstName || '';
          const lastName = user.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          
          if (fullName) {
            displayName = fullName;
          } else if (user.username) {
            displayName = user.username;
          }
        }
        
        return (
          <div className="flex items-center gap-2">
            <UserOutlined className="text-gray-500" />
            <span>{displayName}</span>
          </div>
        );
      },
      width: 180,
    },
    {
      title: 'Time Period',
      key: 'timePeriod',
      render: (_, record) => (
        <div className="text-sm">
          <div>{moment(record.startTime).format('HH:mm')} - {moment(record.endTime).format('HH:mm')}</div>
        </div>
      ),
      width: 120,
    },
    {
      title: 'Hours',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      render: (hours) => {
        const numHours = parseFloat(hours) || 0;
        return (
          <Tag color="blue" icon={<ClockCircleOutlined />}>
            {numHours.toFixed(1)} hrs
          </Tag>
        );
      },
      sorter: (a, b) => (parseFloat(a.hoursWorked) || 0) - (parseFloat(b.hoursWorked) || 0),
      width: 100,
    },
    {
      title: 'Payment',
      key: 'payment',
      render: (_, record) => {
        const hours = parseFloat(record.hoursWorked) || 0;
        const hourlyRate = parseFloat(record.user?.hourlyRate) || 0;
        const payment = hours * hourlyRate;
        
        if (hourlyRate > 0) {
          return (
            <Tag color="green">
              ${payment.toFixed(2)}
            </Tag>
          );
        }
        return <span className="text-gray-400">No rate</span>;
      },
      sorter: (a, b) => {
        const paymentA = (parseFloat(a.hoursWorked) || 0) * (parseFloat(a.user?.hourlyRate) || 0);
        const paymentB = (parseFloat(b.hoursWorked) || 0) * (parseFloat(b.user?.hourlyRate) || 0);
        return paymentA - paymentB;
      },
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
      render: (description) => description || <span className="text-gray-400">No description</span>,
    },
  ];


  return (
    <div className="p-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Timesheet Records</h3>
            <p className="text-sm text-gray-600">Includes all timesheet entries from this plan and all split plans</p>
          </div>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
        
      </div>

      <Card title="Consolidated Timesheet Entries (All Split Plans Included)">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={timesheets}
            rowKey="id"
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        </Spin>
      </Card>

    </div>
  );
}

export default PlanTimesheetSummary;
